import { GestionarContactosInformeDialogComponent } from 'src/app/presentation/shared/contactos/gestionar-contactos-informe-dialog.component';
import { gestionContactosInformeSiAplica } from 'src/app/presentation/shared/contactos/contactos-informe.utils';
import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { MessagesModule } from 'primeng/messages';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  CrearTareaSinOcDialogComponent,
  TareaCreada,
  IngresoCumplimientoPayload
} from './crear-tarea-sin-oc-dialog.component';
import { IngresarGestionDialogComponent } from './ingresar-gestion-dialog.component';
import {
  GestionLlamadaResponse,
  OrdenServicioDetalleHidratada
} from 'src/app/domain/model/sin-oc/sin-oc';
import { ResultadoCargaMasivaComponent } from '../cumplimiento/resultado-carga-masiva.component';
import { GestionesMasivasDialogComponent } from './gestiones-masivas-dialog.component';
import { CumplimientosMasivasDialogComponent } from './cumplimientos-masivos-dialog.component';
import { DatosBase } from './models/datos-base.model';
import { FormularioCumplimientoComponent } from '../cumplimientos/components/formulario-cumplimiento/formulario-cumplimiento.component';
import { HistorialCumplimientosDialogComponent } from '../cumplimientos/components/historial-cumplimientos-dialog/historial-cumplimientos-dialog.component';
import {
  CumplimientoRequest,
  CumplimientoResponse,
  DatosActaRequest,
  DatosSinOcCumplimiento,
  ProgramacionPendiente,
  HistorialCumplimiento
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import { JUSTIFICACIONES_REPROGRAMACION, TIPO_PLAN } from 'src/app/common/constants-ejb';
import { abrirAlertaTemprana } from 'src/app/common/alerta-temprana.utils';
import { avisarPostPersistencia } from 'src/app/common/aviso-post-persistencia';
import {
  filtrarTiposCitaPorClasificacion,
  tipoCitaPermitidoParaClasificacion
} from 'src/app/common/tipo-cita.utils';

interface UltimoCumplimientoRegistrado {
  nmconsecutivoCumplimiento: number;
  nmconsecutivoManualOrden: string | null;
}

/**
 * Componente Página: SinOrientacionServiciosComponent
 *
 * Página principal del módulo "Sin Orientación de Servicios".
 * Muestra el formulario directamente (sin diálogo intermedio),
 * tal como lo hace el legacy GWT CumplimientoSinOcViewImpl.java.
 *
 * Ruta: /sin-orientacion-servicios
 */
@Component({
  selector: 'app-sin-orientacion-servicios',
  standalone: true,
  imports: [
    GestionarContactosInformeDialogComponent,
    CommonModule,
    CardModule,
    MessagesModule,
    DialogModule,
    ToastModule,
    CrearTareaSinOcDialogComponent,
    IngresarGestionDialogComponent,
    ResultadoCargaMasivaComponent,
    GestionesMasivasDialogComponent,
    CumplimientosMasivasDialogComponent,
    FormularioCumplimientoComponent,
    HistorialCumplimientosDialogComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast position="top-right" />
    <!-- Legacy: gestión de contactos del informe tras guardar un informe Final -->
    <app-gestionar-contactos-informe-dialog #contactosInformeDialog />
    <div class="p-3 sm:p-4 lg:p-6 max-w-5xl mx-auto overflow-x-hidden">
      <!-- Breadcrumb -->
      <div
        class="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"
      >
        <span>Cumplimientos</span>
        <span class="text-gray-400">»</span>
        <span class="font-medium text-gray-800 dark:text-gray-200"
          >Cumplimiento sin Orientación de Servicio</span
        >
      </div>

      <!-- Título de módulo (igual que el legacy: breadcrumb title) -->
      <div class="mb-4">
        <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
          Sin orientación de servicio
        </h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Registro de tarea o cumplimiento sin orientación de servicio
        </p>
      </div>

      <!-- Panel de resultado de última gestión creada -->
      @if (ultimaGestionCreada()) {
        <div
          class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg flex items-start gap-3"
        >
          <i
            class="pi pi-check-circle text-green-600 dark:text-green-400 text-xl mt-0.5"
          ></i>
          <div class="text-sm">
            <p class="font-semibold text-green-800 dark:text-green-300 mb-1">
              Gestión registrada exitosamente
            </p>
            @if (ultimaGestionCreada()!.nmconsecutivo) {
              <span class="text-gray-700 dark:text-gray-300">
                <span class="font-medium">Consecutivo:</span>
                {{ ultimaGestionCreada()!.nmconsecutivo }}
              </span>
            }
          </div>
        </div>
      }

      <!-- Panel de resultado de última tarea creada -->
      @if (ultimaTareaCreada()) {
        <div
          class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg flex items-start gap-3"
        >
          <i
            class="pi pi-check-circle text-green-600 dark:text-green-400 text-xl mt-0.5"
          ></i>
          <div class="text-sm">
            <p class="font-semibold text-green-800 dark:text-green-300 mb-1">
              Tarea creada exitosamente
            </p>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-gray-700 dark:text-gray-300"
            >
              <span
                ><span class="font-medium">Orden:</span>
                {{ ultimaTareaCreada()!.dsOrden }}</span
              >
              <span
                ><span class="font-medium">Plan:</span>
                {{ ultimaTareaCreada()!.dsPlan }}</span
              >
              <span
                ><span class="font-medium">Versión:</span>
                {{ ultimaTareaCreada()!.dsVersion }}</span
              >
              <span
                ><span class="font-medium">Actividad:</span>
                {{ ultimaTareaCreada()!.dsActividad }}</span
              >
            </div>
          </div>
        </div>
      }

      <!-- Panel de resultado de último cumplimiento registrado -->
      @if (ultimoCumplimientoRegistrado()) {
        <div
          class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg flex items-start gap-3"
        >
          <i
            class="pi pi-check-circle text-green-600 dark:text-green-400 text-xl mt-0.5"
          ></i>
          <div class="text-sm">
            <p class="font-semibold text-green-800 dark:text-green-300 mb-1">
              Cumplimiento registrado exitosamente
            </p>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-gray-700 dark:text-gray-300"
            >
              @if (ultimoCumplimientoRegistrado()!.nmconsecutivoManualOrden) {
                <span>
                  <span class="font-medium">Orientación de servicio:</span>
                  {{ ultimoCumplimientoRegistrado()!.nmconsecutivoManualOrden }}
                </span>
              }
              <span>
                <span class="font-medium">Cumplimiento:</span>
                {{ ultimoCumplimientoRegistrado()!.nmconsecutivoCumplimiento }}
              </span>
            </div>
          </div>
        </div>
      }

      <!-- Formulario inline (sin diálogo, igual que el legado) -->

      <p-card>
        <app-crear-tarea-sin-oc-dialog
          (onCreada)="onTareaCreada($event)"
          (onAbrirGestion)="abrirModalGestion()"
          (onAbrirResultadoCargaMasiva)="abrirModalResultadoCargaMasiva()"
          (onAbrirGestionesMasivas)="abrirModalGestionesMasivas()"
          (onAbrirCumplimientosMasivos)="abrirModalCumplimientosMasivos()"
          (onAbrirIngresoCumplimiento)="
            abrirFormularioCumplimientoNoProg($event)
          "
          (onDatosBase)="onDatosBase($event)"
        />
      </p-card>

      <!-- Modal Ingresar Gestión -->
      @if (modalGestionVisible()) {
        <app-ingresar-gestion-dialog
          [datosBase]="datosBase"
          (gestionCreada)="onGestionCreada($event)"
          (onClose)="cerrarModalGestion()"
        />
      }

      <!-- Modal Gestiones Masivas -->
      <!-- Legacy: saMasivaGestiones — CumplimientoSinOcViewImpl.java -->
      @if (modalGestionesMasivasVisible()) {
        <app-gestiones-masivas-dialog
          [visible]="modalGestionesMasivasVisible()"
          (onClose)="cerrarModalGestionesMasivas()"
        />
      }

      <!-- Modal Cumplimientos Masivos -->
      <!-- Legacy: saCumplimientoSinOCMasivo — CumplimientoSinOcViewImpl.java -->
      @if (modalCumplimientosMasivasVisible()) {
        <app-cumplimientos-masivos-dialog
          [visible]="modalCumplimientosMasivasVisible()"
          (onClose)="cerrarModalCumplimientosMasivos()"
        />
      }

      <!-- Modal Resultado Carga Masiva -->
      <p-dialog
        header="Resultado Carga Masiva"
        [(visible)]="modalResultadoCargaMasivaVisible"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '75rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
        [draggable]="false"
        [resizable]="false"
      >
        @if (modalResultadoCargaMasivaVisible) {
          <app-resultado-carga-masiva />
        }
      </p-dialog>

      <!-- HU1132420 — Formulario de cumplimiento NO programado -->
      <app-formulario-cumplimiento
        [visible]="dialogCumplimientoVisible()"
        [programacion]="programacionSinOc()"
        [saldoActual]="programacionSinOc()?.saldoPorEjecutar ?? 0"
        [loading]="loadingGuardarCumplimiento()"
        [flujo]="'NO_PROGRAMADA'"
        [ocultarCamposSinOcSinServicio]="true"
        [esLineaAtencion]="esLineaAtencion()"
        [esTipoCampo]="false"
        [esTipoInforme]="false"
        [requiereJustificacion]="false"
        [justificaciones]="justificaciones"
        [tiposCita]="tiposCitaNoProgramada()"
        [onObtenerMunicipio]="obtenerMunicipioPorCodigo"
        [actaError]="actaError()"
        [mostrarBotonReintentar]="mostrarBotonReintentar()"
        [actaGenerando]="actaGenerando()"
        [mostrarBotonDescargarActa]="mostrarBotonDescargarActa()"
        [mantenerGuardar]="true"
        [descargandoActa]="descargandoActaAsociada()"
        (visibleChange)="dialogCumplimientoVisible.set($event)"
        (guardar)="guardarCumplimientoNoProgramado($event)"
        (verHistorial)="onVerHistorialCumplimientos()"
        (reintentar)="onReintentarActa($event)"
        (descargarActa)="onDescargarActaAsociada()"
        (reportarAlertaTemprana)="reportarAlertaTemprana()"
        (cancelar)="cerrarFormularioCumplimientoNoProg()"
      >
      </app-formulario-cumplimiento>

      <app-historial-cumplimientos-dialog
        [visible]="historialCumplimientosVisible()"
        [cumplimientos]="historialCumplimientos()"
        (cerrar)="historialCumplimientosVisible.set(false)"
        (recargarHistorial)="onVerHistorialCumplimientos()"
      >
      </app-historial-cumplimientos-dialog>
    </div>
  `
})
export class SinOrientacionServiciosComponent {
  @ViewChild('contactosInformeDialog') contactosInformeDialog?: GestionarContactosInformeDialogComponent;
  private readonly cumplimientoRepo = inject(CumplimientoRepository);
  private readonly municipioRepo = inject(MunicipioRepository);
  private readonly agendaRepo = inject(AgendaRepository);
  private readonly messageService = inject(MessageService);

  ultimaTareaCreada = signal<TareaCreada | null>(null);
  ultimaGestionCreada = signal<GestionLlamadaResponse | null>(null);
  ultimoCumplimientoRegistrado = signal<UltimoCumplimientoRegistrado | null>(
    null
  );
  modalGestionVisible = signal(false);
  modalResultadoCargaMasivaVisible = false;
  modalGestionesMasivasVisible = signal(false);
  modalCumplimientosMasivasVisible = signal(false);
  datosBase: DatosBase | null = null;

  // === HU1132420 — Estado del formulario de cumplimiento NO programado ===
  dialogCumplimientoVisible = signal(false);
  loadingGuardarCumplimiento = signal(false);
  programacionSinOc = signal<ProgramacionPendiente | null>(null);
  tiposCitaNoProgramada = signal<Array<{ label: string; value: number }>>([]);
  /**
   * Clasificación (tipo de orden de servicio) de la tarea con la que se abrió el
   * formulario de cumplimiento. Gobierna qué tipos de cita son válidos:
   * Capacitación → solo Campo; Asesoría (y demás) → Campo e Informe.
   */
  private clasificacionSinOc: { cdclasificacion: string; dsclasificacion: string } = {
    cdclasificacion: '',
    dsclasificacion: ''
  };
  actaError = signal<string | null>(null);
  actaGenerando = signal(false);
  mostrarBotonReintentar = signal(false);
  mostrarBotonDescargarActa = signal(false);
  descargandoActaAsociada = signal(false);
  historialCumplimientosVisible = signal(false);
  historialCumplimientos = signal<HistorialCumplimiento[]>([]);
  private nmconsecutivoCumplimientoGuardado: number | null = null;
  private nmconsecutivoCumplimientoDescargable: number | null = null;
  private nmconsecutivoProgramacionActual: number | null = null;
  /** DNI del asesor cargado en la orden hidratada (para enviar en el cumplimiento) */
  private dniAsesorOrden: string | null = null;
  /**
   * Payload `datosSinOc` que viaja en el request al backend. Se construye de
   * forma atómica con `programacionSinOc` al abrir el formulario y se limpia
   * al cerrar; así no puede desincronizarse del estado visible.
   */
  readonly datosSinOcCache = signal<DatosSinOcCumplimiento | null>(null);

  /**
   * Línea de atención: la forma de entrega existe y no es DOMICILIO ni SKYPE_EMPRESARIAL. En ese caso el
   * formulario pide la observación del correo al contacto. Legacy: isCumpLineaAt (ViewImpl:1233-1234).
   */
  readonly esLineaAtencion = computed(() => {
    const forma = this.datosSinOcCache()?.cdformaEntrega;
    return (
      !!forma &&
      forma.toUpperCase() !== TIPO_PLAN.DOMICILIO &&
      forma.toUpperCase() !== TIPO_PLAN.SKYPE_EMPRESARIAL
    );
  });

  readonly justificaciones = JUSTIFICACIONES_REPROGRAMACION;

  /** Callback de obtención de municipio por código DANE — pasada al formulario. */
  readonly obtenerMunicipioPorCodigo = (codigo: string) =>
    this.municipioRepo
      .getByCodigo(codigo)
      .then((m) => ({
        cdmunicipio: m.cdmunicipio,
        cdsura: m.cdsura || '',
        dsmunicipio: m.dsmunicipio || ''
      }))
      .catch(() => null);

  onTareaCreada(tarea: TareaCreada): void {
    this.ultimaTareaCreada.set(tarea);
  }

  onDatosBase(datos: DatosBase) {
    this.datosBase = { ...datos };
  }

  abrirModalGestion() {
    this.modalGestionVisible.set(true);
  }

  cerrarModalGestion() {
    this.modalGestionVisible.set(false);
  }

  abrirModalResultadoCargaMasiva() {
    this.modalResultadoCargaMasivaVisible = true;
  }

  abrirModalGestionesMasivas() {
    this.modalGestionesMasivasVisible.set(true);
  }

  cerrarModalGestionesMasivas() {
    this.modalGestionesMasivasVisible.set(false);
  }

  abrirModalCumplimientosMasivos() {
    this.modalCumplimientosMasivasVisible.set(true);
  }

  cerrarModalCumplimientosMasivos() {
    this.modalCumplimientosMasivasVisible.set(false);
  }

  onGestionCreada(event: GestionLlamadaResponse) {
    this.ultimaGestionCreada.set(event);
    this.cerrarModalGestion();
  }

  // ── HU1132420 ──────────────────────────────────────────────────────────────
  /**
   * Recibe el payload del dialog "Crear Tarea Sin OC" (botón
   * "Ingresar Cumplimiento") y abre el FormularioCumplimientoComponent en
   * flujo NO_PROGRAMADA. El payload incluye la orden hidratada del backend y
   * datos capturados en el formulario (aseguradora) que deben
   * mostrarse como Saldo por Ejecutar / Aseguradora en el formulario de
   * cumplimiento.
   */
  async abrirFormularioCumplimientoNoProg(
    payload: IngresoCumplimientoPayload | OrdenServicioDetalleHidratada
  ): Promise<void> {
    const hidratada: OrdenServicioDetalleHidratada =
      (payload as IngresoCumplimientoPayload).hidratada ??
      (payload as OrdenServicioDetalleHidratada);
    const aseguradora =
      (payload as IngresoCumplimientoPayload).dsnombreAseguradora ?? '';
    const cliente =
      (payload as IngresoCumplimientoPayload).dsnombreCliente ?? '';
    const dsclasificacion =
      (payload as IngresoCumplimientoPayload).dsclasificacion ??
      hidratada.dsclasificacion;
    const cdclasificacion =
      (payload as IngresoCumplimientoPayload).cdclasificacion ?? '';

    this.clasificacionSinOc = {
      cdclasificacion: cdclasificacion,
      dsclasificacion: dsclasificacion ?? ''
    };
    await this.cargarTiposCitaNoProgramada(cdclasificacion, dsclasificacion);
    this.resetearEstadoActa();
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;
    this.datosSinOcCache.set(this.construirDatosSinOc(hidratada));
    this.programacionSinOc.set(
      this.mapearHidratadaAProgramacion(
        hidratada,
        aseguradora,
        cliente,
        dsclasificacion
      )
    );
    this.nmconsecutivoProgramacionActual = null;
    this.dniAsesorOrden = null;
    this.dialogCumplimientoVisible.set(true);
  }

  cerrarFormularioCumplimientoNoProg(): void {
    this.dialogCumplimientoVisible.set(false);
    this.programacionSinOc.set(null);
    this.dniAsesorOrden = null;
    this.datosSinOcCache.set(null);
    this.mostrarBotonDescargarActa.set(false);
    this.descargandoActaAsociada.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;
    this.nmconsecutivoProgramacionActual = null;
    this.historialCumplimientosVisible.set(false);
    this.historialCumplimientos.set([]);
    this.resetearEstadoActa();
  }

  async reportarAlertaTemprana(): Promise<void> {
    await abrirAlertaTemprana(this.programacionSinOc(), this.messageService);
  }

  async guardarCumplimientoNoProgramado(
    request: CumplimientoRequest
  ): Promise<void> {
    if (this.loadingGuardarCumplimiento()) {
      return;
    }

    // Guard de último cinturón: en este flujo el backend exige `datosSinOc`.
    // Si por alguna razón se perdió el estado, evitamos disparar un POST que
    // el use case rechazaría con BusinessException("Faltan los datos de la
    // orden de servicio para registrar el cumplimiento sin OC").
    const datosSinOc = this.datosSinOcCache();
    if (!datosSinOc) {
      console.error(
        '[SinOC] guardarCumplimientoNoProgramado sin datosSinOcCache. Estado:',
        {
          dialogCumplimientoVisible: this.dialogCumplimientoVisible(),
          tieneProgramacion: !!this.programacionSinOc(),
          tieneDniAsesor: !!this.dniAsesorOrden
        }
      );
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail:
          'No fue posible recuperar los datos de la orden Sin OC. Cierre y vuelva a abrir Ingresar Cumplimiento.',
        life: 6000
      });
      return;
    }

    // Regla de negocio: una orden de servicio de tipo Capacitación solo admite
    // cumplimientos con tipo de cita "Campo". El combo ya viene filtrado, este
    // guard cubre el caso en que el catálogo llegara sin filtrar. El backend
    // valida lo mismo de forma independiente.
    if (
      !tipoCitaPermitidoParaClasificacion(
        request.cdtipoCita,
        this.clasificacionSinOc.cdclasificacion,
        this.clasificacionSinOc.dsclasificacion
      )
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Tipo de cita no permitido',
        detail:
          'Las órdenes de servicio de tipo Capacitación solo admiten cumplimientos con tipo de cita Campo.',
        life: 6000
      });
      return;
    }

    this.loadingGuardarCumplimiento.set(true);
    if (this.dniAsesorOrden) {
      request.dniAsesor = this.dniAsesorOrden;
    }
    // Flujo Sin OC: la OC todavía NO existe; el backend la crea al guardar.
    request.cumplimientoSinOc = true;
    request.nmconsecOrdenServDetalle = undefined;
    request.datosSinOc = datosSinOc;
    this.resetearEstadoActa();
    try {
      const response: CumplimientoResponse =
        await this.cumplimientoRepo.registrarCumplimiento(request);
      avisarPostPersistencia(this.messageService, response);
      // Legacy (CreateOrUpdateCumplimientoActivity.gestionarContactosInforme): informe Final →
      // gestión de contactos del informe
      const contactosInformePendiente = gestionContactosInformeSiAplica(request, response);
      if (contactosInformePendiente) {
        void this.contactosInformeDialog?.abrir(
          contactosInformePendiente.radicado,
          contactosInformePendiente.cumplimientos
        );
      }
      const nmconsecutivoProgramacion = (response as any)
        .nmconsecutivoProgramacion as number | undefined;
      if (nmconsecutivoProgramacion && nmconsecutivoProgramacion > 0) {
        this.nmconsecutivoProgramacionActual = nmconsecutivoProgramacion;
        const actual = this.programacionSinOc();
        if (actual && actual.nmconsecutivo !== nmconsecutivoProgramacion) {
          this.programacionSinOc.set({
            ...actual,
            nmconsecutivo: nmconsecutivoProgramacion
          });
        }
      }
      this.registrarUltimoCumplimiento(response);

      if (response.asociarActa && response.idActa360 == null) {
        this.nmconsecutivoCumplimientoGuardado = response.nmconsecutivo;
        this.mostrarBotonReintentar.set(true);

        const detalleBackend = response.actaMensajeError ?? undefined;
        const mensajeAlerta = detalleBackend?.trim()
          ? detalleBackend
          : 'El cumplimiento fue guardado, pero no se pudo generar el acta 360.';

        this.actaError.set(mensajeAlerta);
        this.messageService.add({
          severity: 'success',
          summary: 'Cumplimiento registrado',
          detail:
            'Cumplimiento registrado correctamente. Puede reintentar la generación del acta.',
          life: 5000
        });
        this.messageService.add({
          severity: 'warn',
          summary: 'Acta pendiente',
          detail: mensajeAlerta,
          life: 8000
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Cumplimiento registrado',
        detail: 'Cumplimiento registrado correctamente.',
        life: 4000
      });
      if (response.idActa360) {
        this.nmconsecutivoCumplimientoDescargable = response.nmconsecutivo;
        this.mostrarBotonDescargarActa.set(true);
        this.messageService.add({
          severity: 'success',
          summary: 'Acta asociada',
          detail: `El acta ${response.idActa360} fue generada y asociada al cumplimiento.`,
          life: 4000
        });
        return;
      }
      this.cerrarFormularioCumplimientoNoProg();
    } catch (err: unknown) {
      const e = err as {
        error?: { message?: string; detail?: string };
        message?: string;
      };
      const msg =
        e?.error?.message ??
        e?.error?.detail ??
        e?.message ??
        'Error al registrar el cumplimiento.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: msg,
        life: 8000
      });
    } finally {
      this.loadingGuardarCumplimiento.set(false);
    }
  }

  async onVerHistorialCumplimientos(): Promise<void> {
    const consecutivoProgramacion =
      this.nmconsecutivoProgramacionActual ??
      this.programacionSinOc()?.nmconsecutivo ??
      null;
    if (!consecutivoProgramacion || consecutivoProgramacion <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin historial',
        detail:
          'Este registro aún no tiene una programación asociada para consultar historial.',
        life: 5000
      });
      return;
    }

    try {
      const cumplimientos =
        await this.cumplimientoRepo.listarHistorialCumplimientos(
          consecutivoProgramacion
        );
      this.historialCumplimientos.set(cumplimientos);
      this.historialCumplimientosVisible.set(true);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el historial de cumplimientos',
        life: 5000
      });
    }
  }

  /** HU1132419 — Reintenta la generación del acta 360 cuando falla en el guardado inicial. */
  async onReintentarActa(datosActa: DatosActaRequest): Promise<void> {
    if (this.actaGenerando()) {
      return;
    }

    if (!this.nmconsecutivoCumplimientoGuardado) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Acta pendiente',
        detail:
          'No hay un cumplimiento guardado para reintentar la generación del acta.',
        life: 5000
      });
      return;
    }

    this.actaGenerando.set(true);
    this.actaError.set(null);
    const nmconsecutivo = this.nmconsecutivoCumplimientoGuardado;

    try {
      const payload = { ...datosActa, idCumplimiento: String(nmconsecutivo) };
      const result = await this.cumplimientoRepo.reintentarGenerarActa(
        nmconsecutivo,
        payload
      );

      this.mostrarBotonReintentar.set(false);
      this.nmconsecutivoCumplimientoDescargable = nmconsecutivo;
      this.mostrarBotonDescargarActa.set(true);
      this.messageService.add({
        severity: 'success',
        summary: 'Acta asociada',
        detail: `El acta ${result.idActa360} fue generada y asociada al cumplimiento.`,
        life: 5000
      });
    } catch (err: unknown) {
      const e = err as {
        error?: { message?: string; detail?: string };
        message?: string;
      };
      const msg =
        e?.error?.message ??
        e?.error?.detail ??
        e?.message ??
        'Error al reintentar la generación del acta.';
      this.actaError.set(msg);
      this.messageService.add({
        severity: 'error',
        summary: 'Error al generar acta',
        detail: msg,
        life: 8000
      });
    } finally {
      this.actaGenerando.set(false);
    }
  }

  async onDescargarActaAsociada(): Promise<void> {
    const nmconsecutivo = this.nmconsecutivoCumplimientoDescargable;
    if (!nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin acta asociada',
        detail: 'No hay un acta asociada disponible para descargar.'
      });
      return;
    }

    this.descargandoActaAsociada.set(true);
    try {
      await this.cumplimientoRepo.descargarActaPdf(
        nmconsecutivo,
        `acta_${nmconsecutivo}.pdf`
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Descarga iniciada',
        detail: 'El acta asociada se está descargando.'
      });
    } catch (error) {
      const mensaje =
        error instanceof Error
          ? error.message
          : 'No fue posible descargar el acta asociada.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: mensaje
      });
    } finally {
      this.descargandoActaAsociada.set(false);
    }
  }

  /**
   * Proyecta la orden hidratada al payload exacto que el backend espera en
   * `datosSinOc`. Se invoca SOLO al abrir el formulario, garantizando que
   * mientras el diálogo esté visible el cache nunca quede desincronizado.
   */
  private construirDatosSinOc(
    h: OrdenServicioDetalleHidratada
  ): DatosSinOcCumplimiento {
    return {
      nmconsecHomologacion: h.nmconsecHomologacion,
      nmconsecAsesor: Number(h.nmconsecAsesor),
      nmconsecOficina: h.nmconsecOficina,
      cdcontrato: h.cdcontratoBeneficiario,
      cdcentroTrabajo: h.cdcentroTrabajo,
      // Legacy generarOrdenCompra: la OC nace con los municipios del formulario Sin OC.
      cdmunicipioOrigen: h.cdmunicipioOrigen,
      cdmunicipioEntrega: h.cdmunicipioEntrega,
      cdproducto: h.cdproducto,
      dscanalServicio: h.dscanalServicio,
      cdformaEntrega: h.formaEntrega,
      nmexpediente: h.nmexpediente,
      dsestadoInvestigacion: h.dsestadoInvestigacion
    };
  }

  /**
   * Carga el catálogo de tipos de cita de uso SERVICIO y lo restringe según el
   * tipo de orden de servicio (clasificación de la tarea homologada):
   *
   * - Capacitación (`CDCLASIFICACION = '3'`) → únicamente "Campo"; "Informe" no
   *   se ofrece ni puede seleccionarse.
   * - Asesoría (cualquier otra clasificación) → "Campo" e "Informe".
   *
   * Legacy: `CreateOrUpdateCumplimientoViewImpl.clasificacionCapacitacion()`
   * filtra `lbTipoCita` por `NMCONSECUTIVO = 17` cuando la clasificación es "3".
   */
  private async cargarTiposCitaNoProgramada(
    cdclasificacion?: string,
    dsclasificacion?: string
  ): Promise<void> {
    try {
      const tipos = await this.agendaRepo.listarTiposCita(
        undefined,
        'SERVICIO'
      );
      const opciones = filtrarTiposCitaPorClasificacion(
        tipos.map((t) => ({
          label: t.dstipo,
          value: t.nmconsecutivo
        })),
        cdclasificacion,
        dsclasificacion
      );
      this.tiposCitaNoProgramada.set(opciones);
    } catch {
      this.tiposCitaNoProgramada.set([]);
      this.messageService.add({
        severity: 'warn',
        summary: 'Tipos de cita no disponibles',
        detail: 'No fue posible cargar el catálogo de tipos de cita.',
        life: 4000
      });
    }
  }

  private mapearHidratadaAProgramacion(
    h: OrdenServicioDetalleHidratada,
    dsnombreAseguradora = '',
    dsnombreCliente = '',
    dsclasificacion = ''
  ): ProgramacionPendiente {
    return {
      nmconsecutivo: (h as any).nmconsecutivoProgramacion ?? 0,
      nmconsecOrdenServDetalle: 0,
      nmconsecutivoManualOrden: '',
      dsnombreBeneficiario: h.dsnombreBeneficiario,
      dniBeneficiario: h.dniBeneficiario,
      dscentroTrabajo: h.dscentroTrabajo,
      // Legacy crearBenCt: tras SP_CREAR_BENEF_CT la orden queda con el centro de trabajo y el beneficiario
      // devueltos; el buscador de contactos del cumplimiento los usa (lista por centro de trabajo).
      nmconsecBenefCt: h.nmconsecBenefCt,
      nmconsecutivoBeneficiario: h.nmconsecBeneficiario,
      dstarea: dsclasificacion || h.dsclasificacion,
      dsnombreServicio: h.dsproducto,
      cdmunicipioOrigen: h.cdmunicipioOrigen,
      cdmunicipioEntrega: h.cdmunicipioEntrega,
      dsmunicipioOrigen: h.dsmunicipioOrigen,
      dsmunicipioEntrega: h.dsmunicipioEntrega,
      cdsuraOrigen: h.cdsuraOrigen,
      cdsuraEntrega: h.cdsuraEntrega,
      nmconsecutivoOficina: h.nmconsecOficina,
      nmconsecutivoCliente: h.nmconsecCliente,
      dsnombreCliente: dsnombreCliente || h.dsnombreCliente,
      cdpersona: h.cdpersonaBeneficiario,
      cdclasificacion: dsclasificacion || h.dsclasificacion,
      dsclasificacion: dsclasificacion || h.dsclasificacion,
      // HU1132420 — datos capturados en el formulario Sin OC:
      //   * Cantidad no pertenece a este flujo (exclusiva de Crear Tarea): saldo = 0.
      //   * Aseguradora = la seleccionada en el formulario Sin OC.
      // Legacy: CumplimientoSinOcViewImpl.onClickIngCumplimiento()
      // Fuerza visualización/obligatoriedad de hora en NO_PROGRAMADA.
      snprogramacionAutomatica: 'N',
      snocautomatica: 'S',
      cumplimientoSinOc: true,
      nmcantidadProgramada: 0,
      saldoPorEjecutar: 0,
      dsnombreAseguradora: dsnombreAseguradora || undefined
    };
  }

  private resetearEstadoActa(): void {
    this.actaError.set(null);
    this.actaGenerando.set(false);
    this.mostrarBotonReintentar.set(false);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoGuardado = null;
    this.nmconsecutivoCumplimientoDescargable = null;
  }

  private registrarUltimoCumplimiento(response: CumplimientoResponse): void {
    const nmconsecutivoManualOrden =
      response.nmconsecutivoManualOrden?.trim() || null;
    this.ultimoCumplimientoRegistrado.set({
      nmconsecutivoCumplimiento: response.nmconsecutivo,
      nmconsecutivoManualOrden
    });
  }
}
